import React, { useEffect, useState } from "react";
import { Button, Card, CardHeader, CardBody, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertFromRaw, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { FileUploader } from "react-drag-drop-files";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { v4 as uuidv4 } from "uuid";
import { useApiStore } from "../../hooks/ApiStoreContext";
import Swal from "sweetalert2";

function AddProduct() {
  const apiStore = useApiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [heading, setHeading] = useState("Add");
  const [categoryOptions, setCategoryOption] = useState([]);
  const [moqQuantityOptions, setMoqQuantityOption] = useState([]);
  const [editor, setEditor] = useState(EditorState.createEmpty());
  const [modalShow, setModalShow] = useState(false);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const fileTypes = ["JPEG", "JPG", "PNG"];
  const [productImages, setProductImages] = useState([]);
  const [displaySequence, setDisplaySequence] = useState("");
  const [productImageId, setProductImageId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      productId: 0,
      name: "",
      code: "",
      categoryId: "",
      price: 0,
      gstIncluded: true,
      gst: 0,
      gstAmount: 0,
      basePrice: 0,
      discount: 0,
      discountAmount: 0,
      priceAfterDiscount: 0,
      gstAmountAfterDiscount: 0,
      totalPrice: 0,
      stock: 0,
      hsnCode: "",
      outOfStockMessage: "",
      isActive: true,
      description: "",
      moq: "1",
      moqQuantity: "1",
      gender: "",
      ageGroup: "",
      fabric: "",
      color: "",
      occasion: "",
      size: "",
      pattern: "",
      setContent: "",
      designNumber: "",
      rateOfPiece: "",
      productImages: [],
    },
  });

  const handleModalClose = () => {
    setFile(null);
    setProductImageId(null);
    setDisplaySequence("");
    setModalShow(false);
  };

  const handleModalShow = () => {
    // Set default sequence to next available number
    const nextSequence = productImages.length > 0 
      ? Math.max(...productImages.map(img => parseInt(img.sequence))) + 1 
      : 1;
    setDisplaySequence(nextSequence.toString());
    setModalShow(true);
  };

  const handleFileChange = (file) => {
    setFile(file);
  };

  // const handleFileDownload = () => {
  //     if (file) {
  //         const url = URL.createObjectURL(file);
  //         const link = document.createElement("a");
  //         link.href = url;
  //         link.download = file.name;
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);
  //     }
  // };

  const handleFileUpload = () => {
    if (!file || !displaySequence || isNaN(displaySequence) || parseInt(displaySequence) <= 0) {
      Swal.fire({ icon: "warning", title: "Oops...", text: "Please select a file and set a valid display sequence." });
      return;
    }
    if (productImages.some((img) => img.sequence == displaySequence && img.productImageId !== productImageId)) {
      Swal.fire({ icon: "warning", title: "Oops...", text: "Display sequence must be unique." });
      return;
    }

    const newImage = {
      productImageId: productImageId || uuidv4(),
      productId: getValues("productId"),
      sequence: displaySequence,
      file,
      isModified: true,
    };

    const updatedImages = productImageId
      ? productImages.map((img) => (img.productImageId === productImageId ? newImage : img))
      : [...productImages, newImage];

    setProductImages(updatedImages);
    setValue("productImages", updatedImages);
    setProductImageId(null);
    setDisplaySequence("");
    setFile(null);
    setModalShow(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchCategories(), fetchMOQQuantity()]);
        let prodId = location.pathname.split("/")[2];
        if (prodId) {
          setHeading("Edit");
          setValue("productId", prodId);
          await fetchProductData(prodId);
        } else {
          setHeading("Add");
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load initial data." });
      }
      setIsLoading(false);
    };
    fetchData();
  }, [location, setValue]);

  const fetchCategories = async () => {
    try {
      let response = await apiStore.get("/Categories/GetAllActiveCategories");
      //console.log(response);
      setCategoryOption(response);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching categories:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load categories." });
      }
    }
  };

  const fetchMOQQuantity = async () => {
    try {
      let response = await apiStore.get("/product/GetMOQQuantityList");
      //console.log(response);
      setMoqQuantityOption(response);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching MOQ quantity:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load MOQ quantities." });
      }
    }
  };

  const fetchProductData = async (prodId) => {
    try {
      const response = await apiStore.get(`/product/${prodId}`);
      setValue("name", response.name);
      setValue("code", response.code);
      setValue("categoryId", String(response.categoryId));
      setValue("price", response.price);
      setValue("gstIncluded", response.gstIncluded);
      setValue("gst", response.gst);
      setValue("gstAmount", response.gstAmount);
      setValue("basePrice", response.basePrice);
      setValue("discount", response.discount);
      setValue("discountAmount", response.discountAmount);
      setValue("priceAfterDiscount", response.priceAfterDiscount);
      setValue("gstAmountAfterDiscount", response.gstAmountAfterDiscount);
      setValue("totalPrice", response.totalPrice);
      setValue("hsnCode", response.hsnCode);
      setValue("stock", response.stock);
      setValue("outOfStockMessage", response.outOfStockMessage);
      setValue("isActive", response.isActive);
      setValue("description", response.description);
      setValue("moq", response.moq);
      setValue("moqQuantity", response.moqQuantity);
      setValue("gender", response.gender);
      setValue("ageGroup", response.ageGroup);
      setValue("fabric", response.fabric);
      setValue("color", response.color);
      setValue("occasion", response.occasion);
      setValue("size", response.size);
      setValue("pattern", response.pattern);
      setValue("setContent", response.setContent);
      setValue("designNumber", response.designNumber);
      setValue("rateOfPiece", response.rateOfPiece);
      const { contentBlocks, entityMap } = htmlToDraft(response.description);
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditor(EditorState.createWithContent(contentState));

      const promises = response.productImages.map(async (element) => {
        const imageBlob = await apiStore.getBlob(`/product/getImage/${element.productImageId}`);
        const filename = element.imageSource.split("/").pop();
        const type = imageBlob.type;
        element.file = new File([imageBlob], filename, { type });
        element.isModified = false;
        return element;
      });

      const images = await Promise.all(promises);
      setProductImages(images);
      setValue("productImages", images);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching product:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load product data." });
      }
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/product");
  };

  const onEditorStateChange = (editor) => {
    setEditor(editor);
    //console.log(draftToHtml(convertToRaw(editor.getCurrentContent())));
    setValue("description", draftToHtml(convertToRaw(editor.getCurrentContent())));
  };

  const handleImageEdit = (e, id) => {
    e.preventDefault();
    const prod = productImages.find((obj) => obj.productImageId === id);
    setDisplaySequence(prod.sequence);
    setProductImageId(prod.productImageId);
    setFile(prod.file);
    setModalShow(true);
  };

  const handleImageDelete = (e, id) => {
    e.preventDefault();
    const newImageList = productImages.filter((obj) => obj.productImageId !== id);
    setProductImages(newImageList);
    setValue("productImages", newImageList);
  };

  const handleFileDownloadFromProductList = (e, id) => {
    e.preventDefault();
    const prod = productImages.find((obj) => obj.productImageId === id);
    if (prod?.file) {
      const url = URL.createObjectURL(prod.file);
      const link = document.createElement("a");
      link.href = url;
      link.download = prod.file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const onSubmit = async (data) => {
    //alert('submit called');
    console.log(data);
    //console.log(productImages);
    //Logic to save data

    if (data.productImages.length < 1) {
      await Swal.fire({ icon: "warning", title: "Oops...", text: "Please add at least one image." });
      return;
    }

    const updatedImages = await Promise.all(
      data.productImages.map(async (element) => {
        if (element.isModified) {
          const formData = new FormData();
          formData.append("image", element.file);

          if (element.productImageId && element.imageSource) {
            try {
              await apiStore.delete(`/product/deleteImage/${element.productImageId}`);
            } catch (error) {
              if (error.response?.status !== 401) {
                console.error("Error deleting product image:", error);
              }
            }
          }

          const addImageResponse = await apiStore.postFormData("/product/addImage", formData);
          return { ...element, imageSource: addImageResponse.imageSource, isModified: false };
        }
        return element;
      })
    );

    const updatedData = { ...data, productImages: updatedImages };

    try {
      if (!data.productId) {
        await apiStore.post("/product", updatedData);
        await Swal.fire({ title: "Success", text: "Product saved successfully!", icon: "success" });
      } else {
        await apiStore.put(`/product/${data.productId}`, updatedData);
        await Swal.fire({ title: "Updated", text: "Product updated successfully!", icon: "success" });
      }
      navigate("/product");
      resetForm(); // Reset form without confirmation prompt
    } catch (error) {
      console.error("Error saving product:", error);
      await Swal.fire({ icon: "error", title: "Error", text: "Failed to save product." });
    }
  };

  const handleCancel = () => {
    if (getValues("productId")) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will reset all changes.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, reset",
      }).then((result) => {
        if (result.isConfirmed) {
          resetForm();
        }
      });
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    reset({
      productId: 0,
      name: "",
      code: "",
      categoryId: "",
      price: 0,
      gstIncluded: true,
      gst: 0,
      gstAmount: 0,
      basePrice: 0,
      discount: 0,
      discountAmount: 0,
      priceAfterDiscount: 0,
      gstAmountAfterDiscount: 0,
      totalPrice: 0,
      stock: 0,
      hsnCode: "",
      outOfStockMessage: "",
      isActive: true,
      description: "",
      moq: "1",
      moqQuantity: "1",
      gender: "",
      ageGroup: "",
      fabric: "",
      color: "",
      occasion: "",
      size: "",
      pattern: "",
      setContent: "",
      designNumber: "",
      rateOfPiece: "",
      productImages: [],
    });
    setEditor(EditorState.createEmpty());
    setProductImages([]);
  };

  //Calculation START
  const formatAmount = (num) => (Number(num).toFixed(2).endsWith(".00") ? Math.floor(num).toString() : Number(num).toFixed(2));

  const handlePriceChange = () => {
    handleGSTIncludedChange(getValues("gstIncluded"));
  };

  const handleGSTIncludedChange = (source) => {
    let price = parseFloat(getValues("price") || 0);
    let gstPerc = parseFloat(getValues("gst") || 0);
    let gstAmount = parseFloat(getValues("gstAmount") || 0);
    let basePrice = parseFloat(getValues("basePrice") || 0);
    let discountPerc = parseFloat(getValues("discount") || 0);
    let discountAmount = parseFloat(getValues("discountAmount") || 0);
    let priceAfterDiscount = parseFloat(getValues("priceAfterDiscount") || 0);
    let gstAmountAfterDiscount = parseFloat(getValues("gstAmountAfterDiscount") || 0);
    let totalPrice = parseFloat(getValues("totalPrice") || 0);

    if (source) {
      basePrice = (price / (1 + gstPerc / 100)).toFixed(2);
      let calcGSTAmt = (basePrice * gstPerc) / 100;

      setValue("gstAmount", formatAmount(calcGSTAmt));
      gstAmount = parseFloat(getValues("gstAmount") || 0);

      basePrice = (price - gstAmount).toFixed(2);
      setValue("basePrice", formatAmount(basePrice));
    } else {
      let calcGSTAmt = ((price * gstPerc) / 100).toFixed(2);
      setValue("gstAmount", formatAmount(calcGSTAmt));

      setValue("basePrice", formatAmount(price));
      basePrice = parseFloat(getValues("basePrice") || 0);
    }

    discountAmount = ((basePrice * discountPerc) / 100).toFixed(2);
    setValue("discountAmount", formatAmount(discountAmount));
    discountAmount = parseFloat(getValues("discountAmount") || 0);

    priceAfterDiscount = (basePrice - discountAmount).toFixed(2);
    setValue("priceAfterDiscount", formatAmount(priceAfterDiscount));
    priceAfterDiscount = parseFloat(getValues("priceAfterDiscount") || 0);

    gstAmountAfterDiscount = ((priceAfterDiscount * gstPerc) / 100).toFixed(2);
    setValue("gstAmountAfterDiscount", formatAmount(gstAmountAfterDiscount));
    gstAmountAfterDiscount = parseFloat(getValues("gstAmountAfterDiscount") || 0);

    totalPrice = (priceAfterDiscount + gstAmountAfterDiscount).toFixed(2);
    setValue("totalPrice", formatAmount(totalPrice));
    totalPrice = parseFloat(getValues("totalPrice") || 0);

    ["price", "gst", "gstAmount", "basePrice", "discount", "discountAmount", "priceAfterDiscount", "gstAmountAfterDiscount", "totalPrice"].forEach((field) => {
      const value = getValues(field);
      if (value == null || value === "" || isNaN(value)) {
        setValue(field, 0);
      }
    });
  };

  const handleDiscountChange = (source) => {
    let basePrice = parseFloat(getValues("basePrice") || 0);
    let discountPerc = parseFloat(getValues("discount") || 0);
    let discountAmount = parseFloat(getValues("discountAmount") || 0);
    let totalamount = 0;

    if (source === "Perc" && basePrice > 0) {
      totalamount = ((basePrice * discountPerc) / 100).toFixed(2);
      setValue("discountAmount", formatAmount(totalamount));
    } else if (source === "Amount" && basePrice > 0) {
      totalamount = ((discountAmount / basePrice) * 100).toFixed(2);
      setValue("discount", formatAmount(totalamount));
    } else {
      setValue("discount", 0);
      setValue("discountAmount", 0);
    }

    handleGSTIncludedChange(getValues("gstIncluded"));
  };

  const handleGSTChange = (source) => {
    let price = parseFloat(getValues("price") || 0);
    let gstIncluded = getValues("gstIncluded");
    let gstPerc = parseFloat(getValues("gst") || 0);
    let gstAmount = parseFloat(getValues("gstAmount") || 0);
    let calcGSTPerc = 0;
    let calcGSTAmt = 0;
    let basePrice = 0;

    if (gstIncluded) {
      if (source === "Perc" && price > 0) {
        basePrice = (price / (1 + gstPerc / 100)).toFixed(2);
        calcGSTAmt = ((basePrice * gstPerc) / 100).toFixed(2);
        setValue("gstAmount", formatAmount(calcGSTAmt));
      } else if (source === "Amount" && price > 0) {
        basePrice = (price - gstAmount).toFixed(2);
        calcGSTPerc = ((gstAmount / basePrice) * 100).toFixed(2);
        setValue("gst", formatAmount(calcGSTPerc));
      } else {
        setValue("gstAmount", 0);
        setValue("gst", 0);
        setValue("basePrice", 0);
      }
      setValue("basePrice", formatAmount(basePrice));
    } else {
      setValue("basePrice", formatAmount(price));
      if (source === "Perc" && price > 0) {
        calcGSTAmt = ((price * gstPerc) / 100).toFixed(2);
        setValue("gstAmount", formatAmount(calcGSTAmt));
      } else if (source === "Amount") {
        calcGSTPerc = ((gstAmount / price) * 100).toFixed(2);
        setValue("gst", formatAmount(calcGSTPerc));
      } else {
        setValue("gstAmount", 0);
        setValue("gst", 0);
      }
    }

    handleGSTIncludedChange(gstIncluded);
  };
  //Calculation END

  return (
    <>
      <Row>
        <Col md={12}>
          <Card className="mb-4">
            <CardHeader className="pb-0">
              <Row>
                <Col md={9}>
                  <h4>{heading} Product Details</h4>
                </Col>
                <Col md={3} className="d-grid d-md-flex justify-content-md-end d-sm-block">
                  <Button variant="outline-primary" className="btn-sm" onClick={handleBack}>
                    Back
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="px-0 pt-0 pb-2">
              {isLoading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
                  <Row className="mb-3">
                    <input type="hidden" {...register("productId")} />
                    <Form.Group controlId="productName" className="col-md-6 col-sm-12">
                      <Form.Label>
                        Product name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Product name"
                        {...register("name", { required: "Product name is required." })}
                      />
                      <span className="text-xs text-danger">{errors.name?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="productCode" className="col-md-3 col-sm-12">
                      <Form.Label>
                        Product code <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Product code"
                        {...register("code", {
                          required: "Product code is required.",
                          maxLength: { value: 50, message: "Maximum 50 characters allowed." },
                        })}
                      />
                      <span className="text-xs text-danger">{errors.code?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="designNumber" className="col-md-3 col-sm-12">
                      <Form.Label>Design Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Design Number"
                        {...register("designNumber", {
                          maxLength: { value: 50, message: "Maximum 50 characters allowed." },
                        })}
                      />
                      <span className="text-xs text-danger">{errors.designNumber?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="category" className="col-md-3 col-sm-12">
                      <Form.Label>
                        Category <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        {...register("categoryId", { required: "Category is required." })}
                      >
                        <option value="">Select category</option>
                        {categoryOptions.map((option) => (
                          <option key={option.categoryId} value={option.categoryId}>
                            {option.name}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="text-xs text-danger">{errors.categoryId?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="stock" className="col-md-3 col-sm-12">
                      <Form.Label>
                        Stock <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Stock"
                        {...register("stock", {
                          required: "Stock is required.",
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter a number up to two decimals.",
                          },
                        })}
                      />
                      <span className="text-xs text-danger">{errors.stock?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="moq" className="col-md-3 col-sm-12">
                      <Form.Label>
                        (MOQ) Minimum order quantity <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Minimum order quantity"
                        {...register("moq", {
                          required: "Minimum order quantity is required.",
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Please enter a valid positive integer.",
                          },
                        })}
                      />
                      <span className="text-xs text-danger">{errors.moq?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="moqQuantity" className="col-md-3 col-sm-12">
                      <Form.Label>
                        MOQ quantity <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select 
                        {...register("moqQuantity", { 
                          required: "MOQ quantity is required.",
                          validate: (value) => value !== "" || "Please select a MOQ quantity."
                        })}
                      >
                        <option value="">Select MOQ Quantity</option>
                        {moqQuantityOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="text-xs text-danger">{errors.moqQuantity?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="gender" className="col-md-3 col-sm-12">
                      <Form.Label>
                        Gender <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        {...register("gender", {
                          required: "Gender is required",
                          validate: (value) => value !== "" || "Please select a gender",
                        })}
                      >
                        <option value="" disabled>
                          Select Gender
                        </option>
                        <option value="Boy">Boy</option>
                        <option value="Girl">Girl</option>
                        <option value="Unisex">Unisex</option>
                      </Form.Select>
                      <span className="text-xs text-danger">{errors.gender?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="ageGroup" className="col-md-3 col-sm-12">
                      <Form.Label>Age group</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Age group"
                        {...register("ageGroup")}
                      />
                      <span className="text-xs text-danger">{errors.ageGroup?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="fabric" className="col-md-3 col-sm-12">
                      <Form.Label>Fabric</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Fabric"
                        {...register("fabric")}
                      />
                      <span className="text-xs text-danger">{errors.fabric?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="color" className="col-md-3 col-sm-12">
                      <Form.Label>Color</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Color"
                        {...register("color")}
                      />
                      <span className="text-xs text-danger">{errors.color?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="occasion" className="col-md-3 col-sm-12">
                      <Form.Label>Occasion</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Occasion"
                        {...register("occasion")}
                      />
                      <span className="text-xs text-danger">{errors.occasion?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="size" className="col-md-3 col-sm-12">
                      <Form.Label>Size</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Size"
                        {...register("size")}
                      />
                      <span className="text-xs text-danger">{errors.size?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="pattern" className="col-md-3 col-sm-12">
                      <Form.Label>Pattern</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Pattern"
                        {...register("pattern")}
                      />
                      <span className="text-xs text-danger">{errors.pattern?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="setContent" className="col-md-3 col-sm-12">
                      <Form.Label>Set content</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Set content"
                        {...register("setContent")}
                      />
                      <span className="text-xs text-danger">{errors.setContent?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="rateOfPiece" className="col-md-3 col-sm-12">
                      <Form.Label>Rate of piece</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Rate of piece"
                        {...register("rateOfPiece", {
                          maxLength: { value: 200, message: "Maximum 200 characters allowed." },
                        })}
                      />
                      <span className="text-xs text-danger">{errors.rateOfPiece?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="hsnCode" className="col-md-3 col-sm-12">
                      <Form.Label>HSN code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="HSN Code"
                        {...register("hsnCode")}
                      />
                      <span className="text-xs text-danger">{errors.hsnCode?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="price" className="col-md-3 col-sm-12">
                      <Form.Label>
                        Price <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Price"
                        {...register("price", {
                          required: "Price is required",
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        onBlur={handlePriceChange}
                      />
                      <span className="text-xs text-danger">{errors.price?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="gstIncluded" className="col-md-3 col-sm-12">
                      <Form.Label>GST included in price</Form.Label>
                      <Form.Check
                        type="switch"
                        {...register("gstIncluded")}
                        onChange={(e) => handleGSTIncludedChange(e.target.checked)}
                      />
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="gst" className="col-md-3 col-sm-12">
                      <Form.Label>GST %</Form.Label>
                      <Form.Control
                        placeholder="GST"
                        {...register("gst", {
                          pattern: {
                            value: /^[0-9]{1,3}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        onBlur={() => handleGSTChange("Perc")}
                      />
                      <span className="text-xs text-danger">{errors.gst?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="gstAmount" className="col-md-3 col-sm-12">
                      <Form.Label>GST amount</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="GST amount"
                        {...register("gstAmount", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        onBlur={() => handleGSTChange("Amount")}
                      />
                      <span className="text-xs text-danger">{errors.gstAmount?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="basePrice" className="col-md-3 col-sm-12">
                      <Form.Label>Base Price</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Base Price"
                        {...register("basePrice", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        disabled
                      />
                      <span className="text-xs text-danger">{errors.basePrice?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="discount" className="col-md-3 col-sm-12">
                      <Form.Label>Discount %</Form.Label>
                      <Form.Control
                        placeholder="Discount"
                        {...register("discount", {
                          pattern: {
                            value: /^[0-9]{1,3}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                          max: { value: 100, message: "Maximum 100% is allowed" },
                        })}
                        onBlur={() => handleDiscountChange("Perc")}
                      />
                      <span className="text-xs text-danger">{errors.discount?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="discountAmount" className="col-md-3 col-sm-12">
                      <Form.Label>Discount amount</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Discount amount"
                        {...register("discountAmount", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        onBlur={() => handleDiscountChange("Amount")}
                      />
                      <span className="text-xs text-danger">{errors.discountAmount?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="priceAfterDiscount" className="col-md-3 col-sm-12">
                      <Form.Label>Price after discount</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Price after discount"
                        {...register("priceAfterDiscount", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        disabled
                      />
                      <span className="text-xs text-danger">{errors.priceAfterDiscount?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="gstAmountAfterDiscount" className="col-md-3 col-sm-12">
                      <Form.Label>GST amount after discount</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="GST amount after discount"
                        {...register("gstAmountAfterDiscount", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        disabled
                      />
                      <span className="text-xs text-danger">{errors.gstAmountAfterDiscount?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="totalPrice" className="col-md-3 col-sm-12">
                      <Form.Label>Final price</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Final price"
                        {...register("totalPrice", {
                          pattern: {
                            value: /^[0-9]{1,5}\.?[0-9]{0,2}$/,
                            message: "Please enter number up to two decimals.",
                          },
                        })}
                        disabled
                      />
                      <span className="text-xs text-danger">{errors.totalPrice?.message}</span>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="outOfStockMessage" className="col-md-6 col-sm-12">
                      <Form.Label>Out of stock message</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Out of stock message"
                        {...register("outOfStockMessage")}
                      />
                      <span className="text-xs text-danger">{errors.outOfStockMessage?.message}</span>
                    </Form.Group>

                    <Form.Group controlId="isActive" className="col-md-3 col-sm-12">
                      <Form.Label>Is Active</Form.Label>
                      <Form.Check type="switch" {...register("isActive")} />
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group controlId="description" className="col-md-12 col-sm-12">
                      <Form.Label>Description</Form.Label>
                      <Editor
                        editorState={editor}
                        wrapperClassName="demo-wrapper"
                        editorClassName="demo-editor"
                        toolbarClassName="demo-toolbar"
                        onEditorStateChange={onEditorStateChange}
                        editorStyle={{ height: "200px", overflowY: "auto" }}
                      />
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Button variant="outline-primary" type="button" onClick={handleModalShow}>
                        Add image
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <table style={{ width: "100%" }}>
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-sm font-weight-bolder">Product Image</th>
                            <th className="text-uppercase text-secondary text-sm font-weight-bolder">Display Sequence</th>
                            <th className="text-center text-uppercase text-secondary text-sm font-weight-bolder">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productImages.map((item) => (
                            <tr key={item.productImageId}>
                              <td>
                                {item.file && (
                                  <img
                                    src={URL.createObjectURL(item.file)}
                                    alt="thumbnail"
                                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                  />
                                )}
                              </td>
                              <td>{item.sequence}</td>
                              <td className="text-center">
                                <a
                                  className="me-3 cursor-pointer"
                                  title="Edit"
                                  onClick={(e) => handleImageEdit(e, item.productImageId)}
                                >
                                  <FontAwesomeIcon icon={faPencil} size="lg" />
                                </a>
                                <a
                                  className="me-3 cursor-pointer"
                                  title="Download image"
                                  onClick={(e) => handleFileDownloadFromProductList(e, item.productImageId)}
                                >
                                  <FontAwesomeIcon icon={faDownload} size="lg" />
                                </a>
                                <a
                                  className="text-danger cursor-pointer"
                                  title="Delete"
                                  onClick={(e) => handleImageDelete(e, item.productImageId)}
                                >
                                  <FontAwesomeIcon icon={faTrashCan} size="lg" />
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className="d-grid gap-2 d-md-flex flex-md-row-reverse">
                      <Button variant="primary" className="me-md-2" type="submit">
                        Submit
                      </Button>
                      <Button variant="dark" type="button" onClick={handleCancel}>
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal show={modalShow} onHide={handleModalClose} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add product image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <input type="hidden" value={productImageId} />
            <Form.Group controlId="displaySequence" className="col-md-6 col-sm-12">
              <Form.Label>
                Display sequence <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Sequence"
                value={displaySequence}
                onChange={(e) => setDisplaySequence(e.target.value)}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group controlId="imageSource" className="col-md-6 col-sm-12">
              <Form.Label>
                Image <span className="text-danger">*</span>
              </Form.Label>
              <FileUploader
                multiple={false}
                handleChange={handleFileChange}
                name="file"
                types={fileTypes}
                fileOrFiles={file ? [file] : null}
              />
              {file ? (
                <div>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="thumbnail"
                    style={{ width: "100px", height: "100px", objectFit: "cover" }}
                  />
                  <p>{file.name}</p>
                </div>
              ) : (
                <p>No file uploaded yet</p>
              )}
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleFileUpload}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AddProduct;
